<script lang="ts">
	import {
		DeviceBrowser,
		DeviceGroup,
		DeviceRPi,
		ProjectorPowerListener,
		TEMPORARY_PLAY_NOW_EVENT_NAME,
		TimetableEvent,
		type Device,
		type Lightshow,
		type Scene,
	} from "luxedo-data"
	import { PlayIcon, StopIcon } from "luxedo-icons"
	import { Toast } from "toaster"
	import { LoadingSpinner } from "misc-ui"
	import { DevicePlaybackManager } from "luxedo-data"
	import { openPopupOverlay } from "overlay"
	import { ScheduleController } from "../../../../routes/schedule/ScheduleController"
	import { SelectedDeviceStore } from "../../../../../stores/SelectedDeviceStore"
	import { get } from "svelte/store"
	import { APIThread } from "luxedo-data/src/modules/device-operation-managers/DeviceAPIThread"
	import { openProjectorMenu } from "../../../../routes/projectors"
	import { ShowOverviewController } from "../ShowOverviewController"
	import PlayOnProjectorNotReady from "./PlayOnProjector_ReadyState.svelte"
	import { Reactivity } from "luxedo-reactivity"
	import { ProjectorPowerFallback } from "../ProjectorPowerFallback.svelte"

	interface Props {
		show: Scene | Lightshow
		device: Device
	}

	let { show, device }: Props = $props()

	let isPlaying = $state(false)
	let isWaiting = $state(false)
	let isOffline = $state(false)
	let isReuploading: boolean = $state()

	$effect.pre(() => {
		Reactivity.useProps(device, ["isDeactivated"])
	})

	let allowAnyway = $derived(ProjectorPowerFallback.allowPlayAnyway)
	let isDeactivated = $derived(Reactivity.propOf(device, "isDeactivated"))

	let activeEvent: TimetableEvent

	ShowOverviewController.subscribe((ctx) => {
		// console.error({ ctx })
		isReuploading = ctx.isReuploading
	})

	DevicePlaybackManager.subscribe((ctx) => {
		if (!device) return

		const devStatus = ctx[device.id]

		if (!devStatus) {
			isOffline = false
			isWaiting = false
			isPlaying = false
			activeEvent = undefined

			return
		}

		const isDevicePlaying = devStatus.isPlaying
		const showScene = devStatus.scene
		const showLightshow = devStatus.lightshow

		// if the device is playing and the show matches this show,
		// OR the device is a device group (this ensures the stop button renders if any child device is in a playback status)
		if (
			isDevicePlaying &&
			(show.id === showScene?.id || show.id === showLightshow?.id || device instanceof DeviceGroup)
		) {
			isPlaying = true
			activeEvent = devStatus.event
		} else {
			isPlaying = false
			activeEvent = undefined
		}
	})

	let listeners: Array<string> = []

	listeners.push(
		device.addUpdateListener((dev) => {
			isOffline = !dev.isOnline
		})
	)

	async function stopPlayback() {
		isWaiting = true

		if (!activeEvent) activeEvent = await DevicePlaybackManager.getActiveEvent(device)

		const clearEvent = async () => {
			return new Promise<void>(async (res, rej) => {
				let listener: string
				let eidosDevice = device

				const resolve = () => {
					res()
					Toast.success("Playback stopped.")
					isPlaying = false
					isWaiting = false
					eidosDevice.removeUpdateListener(listener)
				}

				if (device instanceof DeviceGroup)
					eidosDevice = device.getChildDevices().find((dev) => dev.isOnline) as unknown as Device

				listener = eidosDevice.addUpdateListener(async () => {
					if (DevicePlaybackManager.checkIsPlaying(device) === false) return resolve()
					if ((await DevicePlaybackManager.getActiveEvent(device)) !== activeEvent) return resolve()
				})

				await device.timetableManager.deleteEvent(activeEvent)
				await ScheduleController.Calendar.refreshEvents({
					deviceFilter: get(SelectedDeviceStore),
				})
			})
		}

		try {
			if (activeEvent?.name === TEMPORARY_PLAY_NOW_EVENT_NAME) clearEvent()
			else if (
				(device instanceof DeviceRPi && device.eidos.display_mode === "PREVIEW") ||
				(device instanceof DeviceGroup && DevicePlaybackManager.checkIsPlayingForGroup(device))
			) {
				await DevicePlaybackManager.cancelPreview(device as unknown as Device)
				// await disableButtonForDeviceGroup()
				Toast.success("Playback stopped.")
				isPlaying = false
				isWaiting = false
			} else {
				openPopupOverlay({
					prompt: [
						"This show has been scheduled to play.",
						"To cancel it, please delete the event from the scheduler.",
					],
					onVerify: () => {
						isWaiting = false
					},
				})
			}
		} catch (e) {
			console.error("Error clearing playback... ", e)
			Toast.error("Error canceling playback.")
		}
	}

	const onPlaybackFail = (message: string, showTroubleshoot?: boolean) => {
		if (showTroubleshoot)
			Toast.action(message, "Troubleshoot", () => {
				ShowOverviewController.close()
				openProjectorMenu(device, "Network")
			})
		else Toast.error(message)
	}

	async function playOnDevice() {
		const isGroup = device instanceof DeviceGroup
		try {
			isWaiting = true

			await DevicePlaybackManager.playOnDevice(device, show, {
				onFirstDownload: () => Toast.text("Waiting for show to download..."),
				onPlayingOutdated: () =>
					Toast.text(
						"Playing old version of this show. The playback will restart as soon as the new version is downloaded."
					),
			})

			// await disableButtonForDeviceGroup()
			Toast.success("Playback started")

			isPlaying = true
		} catch (e: any) {
			console.error("Error playing show on device... ", e)
			let errorMessage = "Unable to play show at this time. Please refresh and try again."

			if (e instanceof APIThread.APIThreadFailedError) {
				errorMessage = e.message
				onPlaybackFail(
					errorMessage,
					// only show the "troubleshoot" button if its a group and one of these error types (otherwise navigating to the Network tab would just be confusing)
					isGroup && ["ERR_UNKNOWN", "ERR_TIMEOUT", "ERR_CLIENT_UNREACHABLE"].includes(e.original)
				)
			} else if (e && e.statusCode === 701) {
				errorMessage =
					"Device appears to be disconnected, ensure is connected to Wi-Fi and try again."
				onPlaybackFail(errorMessage, false)
			}
		} finally {
			isWaiting = false
		}
	}

	async function handleClick() {
		if (isOffline) {
			Toast.error("Device appears to be disconnected, ensure is connected to Wi-Fi and try again.")
			return
		}

		if (isWaiting) return

		if (isPlaying) {
			stopPlayback()
		} else {
			playOnDevice()
		}
	}

	function registerListener(dev: Device) {
		dev.addUpdateListener((dev) => {
			isOffline = !dev.isOnline
		})
	}

	let projectorPowerListener = $derived(
		device instanceof DeviceBrowser
			? undefined
			: new ProjectorPowerListener(device as unknown as DeviceRPi)
	)
	let projectorState = $derived(projectorPowerListener?.powerState)
	let isUnknown = $derived(["UNKNOWN", "PARTIAL", "UNDEFINED"].includes(projectorState))
	let isProjectorOn = $derived(["ON"].includes(projectorState))

	registerListener(device)
</script>

{#if !isDeactivated}
	<div class="play-button-container">
		{#if !(device instanceof DeviceBrowser)}
			<PlayOnProjectorNotReady device={device as unknown as DeviceRPi} {isProjectorOn} />
		{/if}
		{#if isWaiting}
			<LoadingSpinner color="var(--color-main)" height="3.5rem" />
		{:else}
			<button
				id="play-lightshow-btn"
				class="icon"
				title={isPlaying ? "Stop Playback" : "Play on Projector"}
				onclick={handleClick}
				disabled={!allowAnyway && !isUnknown && (isOffline || !isProjectorOn || isReuploading)}
			>
				{#if isPlaying}
					<StopIcon />
				{:else}
					<PlayIcon />
				{/if}
			</button>
		{/if}
	</div>
{/if}

<style>
	.play-button-container {
		display: flex;
		flex-direction: row;
		align-items: center;
		z-index: 1;
	}

	.play-button-container :global(.loader) {
		background-color: var(--color-bg-dark);
	}

	#play-lightshow-btn {
		--color-button: var(--color-main);

		outline: 1px solid var(--color-button);
		background-color: var(--color-bg-dark);
		width: 3.5rem;
		height: 3.5rem;
		padding: 1rem;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		transition: transform 250ms;
	}

	#play-lightshow-btn:disabled {
		opacity: 1;
		--color-button: var(--color-main-transparent);
	}

	#play-lightshow-btn:hover,
	#play-lightshow-btn:focus-visible {
		transform: scale(1.1);
	}

	#play-lightshow-btn :global(.svg-fill) {
		fill: var(--color-button);
	}

	:global(.isMobile) #play-lightshow-btn {
		padding: 0.75rem;
	}
</style>
